import PageHero from "@/components/site/PageHero";
import { useSEO } from "@/lib/seo";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    updated: "March 2026",
    body: [
      { h: "Overview", p: "RockLadder Technologies (\"we\", \"our\", \"us\") is committed to protecting the personal information of visitors, prospects, clients, applicants, and website users. This policy describes what data we collect, how we use it, and the rights you have over it." },
      { h: "Data we collect", p: "We collect contact information you provide (name, email, phone, company, role), messages you submit through our forms, resume information for job applications, IP addresses and browser metadata for security and analytics, and cookie preferences you configure." },
      { h: "How we use it", p: "To respond to enquiries, process job applications, send newsletters when you have subscribed, secure our services, meet legal and regulatory obligations, and improve the site." },
      { h: "Lawful bases", p: "Consent (marketing), legitimate interests (site security, prospect response), contract performance (client engagements), and legal obligation." },
      { h: "Retention", p: "Contact and application data is retained for up to 24 months unless a longer period is required by law or the engagement lifecycle." },
      { h: "Your rights (GDPR / DPDP)", p: "You can request access, correction, deletion, portability, or objection at any time. Contact hello@rockladder.com — we will respond within 30 days." },
      { h: "Contact", p: "For privacy questions: privacy@rockladder.com or write to our registered office listed on the Contact page." },
    ],
  },
  terms: {
    title: "Terms of Use",
    updated: "March 2026",
    body: [
      { h: "Acceptance", p: "By accessing or using rockladder.com you agree to these terms. If you do not agree, please do not use the site." },
      { h: "Site content", p: "All content is provided for informational purposes. It does not constitute security advice tailored to your environment. Engage us formally for a scoped assessment." },
      { h: "Intellectual property", p: "All content, marks, and code on this site are owned by RockLadder Technologies or licensed to us. Do not reproduce without written consent." },
      { h: "No warranty", p: "The site is provided as-is. To the maximum extent permitted by law, we disclaim warranties of merchantability, fitness for a particular purpose, and non-infringement." },
      { h: "Governing law", p: "These terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of courts in Gurugram, Haryana." },
    ],
  },
  cookies: {
    title: "Cookies Policy",
    updated: "March 2026",
    body: [
      { h: "What are cookies?", p: "Cookies are small text files stored in your browser. Some are essential to make the site work; others help us measure usage and improve." },
      { h: "Categories", p: "Essential (site function, security), Analytics (aggregated visit metrics), Marketing (only if you consent — used to tailor future content)." },
      { h: "Managing preferences", p: "Use the cookie banner or your browser settings to accept, reject, or customise. Essential cookies cannot be disabled without breaking site function." },
      { h: "Third parties", p: "We may use privacy-friendly analytics and, when enabled, a live-chat provider. These process minimal data on our behalf under contractual protection." },
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    updated: "March 2026",
    body: [
      { h: "Informational content", p: "Content on this site is educational. Nothing on this site should be construed as legal, regulatory, or contractual advice for your specific environment." },
      { h: "Client references", p: "Case studies, testimonials, and metrics are anonymised representations of real engagements. Actual results depend on your program maturity and scope." },
      { h: "External links", p: "Links to third-party sites are provided for convenience. We are not responsible for third-party content." },
      { h: "Security notice", p: "To report a security concern with rockladder.com itself, please contact security@rockladder.com." },
    ],
  },
};

export default function Legal({ kind }) {
  const c = CONTENT[kind] || CONTENT.privacy;

  useSEO({
    title: `${c.title} | RockLadder Technologies`,
    description: `${c.title} for rockladder.com`,
    canonical: `https://rockladder.com/${kind}`,
  });

  return (
    <>
      <PageHero
        eyebrow={`LEGAL · UPDATED ${c.updated.toUpperCase()}`}
        title={c.title}
        breadcrumb={[{ label: c.title }]}
      />
      <section className="relative py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 space-y-10">
          {c.body.map((s) => (
            <div key={s.h}>
              <h2 className="font-display text-xl md:text-2xl text-inkText-primary mb-3">{s.h}</h2>
              <p className="text-sm md:text-base text-inkText-muted leading-[1.85]">{s.p}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
